import React, {FC, useMemo} from 'react';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../../../constants/colors';
import {tabletAndUp} from '../../../../../constants/customMedia';
import {SPACING} from '../../../../../constants/sizing';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../../../constants/typography';
import {SizeClusters} from './SizeClusters';
import {SizeRunCluster, SizeTab} from './types';

interface Props {
  activeFilters: Map<string, Array<string> | undefined>;
  content?: {
    clusters?: Array<SizeRunCluster>;
    tabs?: SizeTab[];
  };
  onTabsReset: (slug: string) => void;
  onSizeClick: (slug: string, sizeId: string) => void;
  onTabClick: (slug: string, sizeId: string) => void;
  className?: string;
  enableFlyoutFilter?: boolean;
}

export const SizeTabsWithClusters: FC<Props> = ({
  activeFilters,
  content,
  onTabsReset,
  onSizeClick,
  onTabClick,
  className,
  enableFlyoutFilter,
}) => {
  const clusters = content?.clusters;
  const tabs = content?.tabs;

  const activeTab = useMemo(() => {
    if (!tabs || !tabs[0]) return undefined;

    const activeTab = tabs.find(tab => {
      const tabSlug = tab.specialSizeFilter?.slug;
      const tabValue = tab.specialSizeFilter?.filterValueId;

      return (
        tabSlug && tabValue && activeFilters.get(tabSlug)?.[0] === tabValue
      );
    });

    return activeTab ?? tabs[0];
  }, [activeFilters, tabs]);

  return (
    <Wrapper className={className}>
      {tabs && (
        <Tabs>
          {tabs.map(tab => {
            const active =
              activeTab?.specialSizeFilter?.filterValueId ===
              tab.specialSizeFilter?.filterValueId;

            return (
              <Tab
                active={active}
                data-testid={`filterFlyoutSizeGroupTab_${
                  tab.specialSizeFilter?.filterValueId ?? 'allSizes'
                }`}
                key={`filter-flyout-group-tab-label-${tab.label}`}
                onClick={() =>
                  tab.specialSizeFilter?.filterValueId
                    ? onTabClick(
                        tab.specialSizeFilter!.slug!,
                        tab.specialSizeFilter!.filterValueId,
                      )
                    : onTabsReset(tab.specialSizeFilter!.slug!)
                }
              >
                {tab.label}
              </Tab>
            );
          })}
        </Tabs>
      )}

      {activeTab && (
        <SizeClusters
          activeFilters={activeFilters}
          clusters={activeTab.clusters}
          onSizeClick={onSizeClick}
          enableFlyoutFilter={enableFlyoutFilter}
        />
      )}

      {clusters && (
        <SizeClusters
          activeFilters={activeFilters}
          clusters={clusters}
          onSizeClick={onSizeClick}
          enableFlyoutFilter={enableFlyoutFilter}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${COLORS.White};
`;

const Tabs = styled.div`
  width: calc(100% + 32px);
  margin: -${SPACING['X.Lg']};
  margin-bottom: 0;
  background-color: ${COLORS.White};
  padding: 0 1rem;
  box-shadow: rgb(0 0 0 / 4%) 0px 10px 15px 0px, rgb(0 0 0 / 8%) 0px 2px 2px 0px;
  display: flex;
  box-sizing: border-box;
  z-index: 1;

  ${tabletAndUp(css`
    width: 100%;
    margin: 0;
  `)}
`;

const Tab = styled.button<{active?: boolean}>`
  position: relative;
  padding: 1.125rem 0 1.25rem;
  margin-right: 1.5rem;
  background-color: transparent;
  border: none;
  display: flex;
  justify-content: center;
  text-align: center;
  color: ${({active}) => (active ? COLORS.Black : COLORS.Grey1)};
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.bold};
  cursor: pointer;

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 1.25rem;
    height: 2px;
    background-color: ${({active}) => (active ? COLORS.Black : 'transparent')};
    transform: translateX(-50%);
  }

  &:hover {
    color: ${COLORS.Black};
  }

  &:focus {
    outline: none;
  }
`;
